import { createSelector } from 'reselect';
import {
  addMinutes,
  format,
  isBefore,
  parseISO,
  startOfMinute,
  startOfWeek,
  endOfWeek,
  subDays,
  subHours,
  subMinutes,
} from 'date-fns';
import { Views } from 'react-big-calendar';
import { ReservationCancellationType } from '@classpass/protobuf-definitions/lib/web/models/partners/reservation';

import { SERVER_DATE_FORMAT, CLASS_ENTITY_TYPE, FEEDBACK_REPORT_AGE_LIMIT_DAYS } from 'constants/logic';
import { adjustDateForTimezone } from 'helpers/intl-fns';
import {
  calendarAvailability as calendarAvailabilityEntity,
  venueInstructors as venueInstructorsEntity,
  messageUser as messageUserEntity,
  conversationLimitStatus as conversationLimitStatusEntity,
} from 'store/saga/entities/bound';
import { selectStartWeekOn } from 'selectors/locale-data';
import {
  selectRouteQuery,
  selectActiveVenue,
  selectActiveVenueId,
  selectCurrentTimeAtVenue,
  selectRouteParams,
} from 'selectors/general';

/*!
 * Calendar Event Mappers
 */

export const CalendarEventType = {
  VENUE_TIME_BLOCK: 'VENUE_TIME_BLOCK',
  UNAVAILABLE_TIME_BLOCK: 'UNAVAILABLE_TIME_BLOCK',
  MSI_TEACHER_TIME_BLOCK: 'MSI_TEACHER_TIME_BLOCK',
  CALENDAR_SYNC_EVENT: 'CALENDAR_SYNC_EVENT',
  RESERVATION: 'RESERVATION',
};

/*!
 * Availability Calendar Selectors
 */

export const selectActiveCalendarView = createSelector(selectRouteParams, ({ view }) => view || Views.DAY);

// selectAvailabilityCalendarDates :: State -> DateRange
export const selectAvailabilityCalendarDates = createSelector(
  selectActiveVenue,
  selectRouteQuery,
  selectActiveCalendarView,
  selectStartWeekOn,
  (state, props) => props,
  ({ timezone }, { startDate }, view, startWeekOn, props) => {
    const todayInVenueTimeZone = adjustDateForTimezone(new Date(), timezone);

    // Map day name abbreviation to a number required by date-fns startOfWeek() and endOfWeek()
    const FIRST_DAY_OF_WEEK_TO_NUMBER = {
      sun: 0,
      mon: 1,
      sat: 6,
    };
    const firstDayOfWeekNumber = FIRST_DAY_OF_WEEK_TO_NUMBER[startWeekOn];

    view = props?.view || view;
    let startDateTime;
    let endDate;

    if (props?.startDate) {
      startDateTime = props.startDate;
    } else if (startDate) {
      startDateTime = parseISO(startDate);
    } else {
      startDateTime = todayInVenueTimeZone;
    }

    if (view === Views.WEEK) {
      startDate = format(startOfWeek(startDateTime, { weekStartsOn: firstDayOfWeekNumber }), SERVER_DATE_FORMAT);
      endDate = format(endOfWeek(startDateTime, { weekStartsOn: firstDayOfWeekNumber }), SERVER_DATE_FORMAT);
    }

    if (view === Views.DAY) {
      startDate = format(startDateTime, SERVER_DATE_FORMAT);
      endDate = startDate;
    }

    return { startDate, endDate };
  }
);

// selectAvailabilitySelectedInstructorId :: State -> Number | undefined
export const selectAvailabilitySelectedInstructorId = createSelector(selectRouteQuery, route => route.practitionerId);

// getRBCEventMapper :: CalendarEventType -> ApointmentAvailability -> RBCEvent
const getRBCEventMapper =
  type =>
  ({ startDatetime, endDatetime, teacherId, ...rest }) => {
    const start = parseISO(startDatetime);
    const end = parseISO(endDatetime);

    return {
      start,
      end,
      type,
      resourceId: teacherId,
      ...rest,
    };
  };

// venueInstructorToRBCSource :: VenueInstructor -> RBCSource
const venueInstructorToRBCSource = venueInstructor => ({
  id: venueInstructor.instructorId,
  openApiId: venueInstructor.openApiId,
  title: venueInstructor.instructorDisplayName || venueInstructor.instructorName,
});

// selectVenueInstructors :: (State, String) -> [venueInstructor]
export const selectAppointmentResources = (state, venueId) => {
  const key = venueInstructorsEntity.defaultKey(null, venueId, CLASS_ENTITY_TYPE.APPOINTMENT);

  return venueInstructorsEntity.loaded(state, key)
    ? venueInstructorsEntity.data(state, key).map(venueInstructorToRBCSource)
    : [];
};

// selectAllEvents :: State -> [RBCEvent]
export function selectAllEvents(state) {
  const venueId = selectActiveVenueId(state);
  const currentTimeAtVenue = selectCurrentTimeAtVenue(state, startOfMinute(new Date()).getTime());
  const activeCalendarView = selectActiveCalendarView(state);
  const { startDate, endDate } = selectAvailabilityCalendarDates(state);
  const key = calendarAvailabilityEntity.defaultKey(null, venueId, startDate, endDate);

  const { loading, data } = calendarAvailabilityEntity.select(state, key) || {};

  if (!data || loading) {
    return [];
  }

  const calendarSyncEvents = data.calendarSyncEvents.map(getRBCEventMapper(CalendarEventType.CALENDAR_SYNC_EVENT));

  const appointmentResources = selectAppointmentResources(state, venueId);
  const openApiIdByInstructorId = appointmentResources.reduce((acc, { id, openApiId }) => {
    if (openApiId != null) {
      acc[id] = openApiId;
    }
    return acc;
  }, {});

  const msiTeacherTimeBlocks = data.msiTeacherTimeBlocks.map(block => ({
    ...getRBCEventMapper(CalendarEventType.MSI_TEACHER_TIME_BLOCK)(block),
    openApiId: openApiIdByInstructorId[block.teacherId],
  }));

  const allResourcesIds = appointmentResources.map(resource => resource.id);

  // Map venue time blocks to all-day events
  // These events will render dropdown for each day in a week view and for each resource in a day view
  const venueTimeBlocks = data.venueTimeBlocks.map(({ startDatetime, endDatetime, description }) => ({
    type: CalendarEventType.VENUE_TIME_BLOCK,
    start: parseISO(startDatetime),
    end: parseISO(endDatetime),
    data: { description },
  }));

  // Map venue time blocks to unavailable time block events
  // These events will render entire day as unavailable
  const venueUnavailableTimeBlocks = data.venueTimeBlocks.map(({ startDatetime, endDatetime }) => ({
    type: CalendarEventType.UNAVAILABLE_TIME_BLOCK,
    start: parseISO(startDatetime),
    // subMinutes() is preventing RBC from interpreting these events as 'all-day' events
    end: subMinutes(parseISO(endDatetime), 1),
  }));

  let venueTimeBlockEvents = [];
  let venueTimeBlockFillerEvents = [];

  // In order to correctly present venue time block on a day view, we need to render them in every resource column
  // Therefore we need to multiply one venue block by every resource
  if (activeCalendarView === Views.DAY) {
    allResourcesIds.forEach(resourceId =>
      venueTimeBlocks.forEach(venueTimeBlock => venueTimeBlockEvents.push({ ...venueTimeBlock, resourceId }))
    );

    allResourcesIds.forEach(resourceId =>
      venueUnavailableTimeBlocks.forEach(venueUnavailableTimeBlock =>
        venueTimeBlockFillerEvents.push({ ...venueUnavailableTimeBlock, resourceId })
      )
    );
  }

  if (activeCalendarView === 'week') {
    venueTimeBlockEvents = venueTimeBlocks;
    venueTimeBlockFillerEvents = venueUnavailableTimeBlocks;
  }

  const appointments = data.reservations.map(
    ({
      attendanceStatus,
      cancellationType,
      createdOn,
      endDatetime,
      firstName,
      lastName,
      lastUpdated,
      startDatetime,
      teacherId,
      visitNumberToVenue,
      ...restAppointment
    }) => {
      const start = parseISO(startDatetime);
      const thirtyMinutesFromNow = addMinutes(currentTimeAtVenue, 30);
      const startsWithinThirtyMinutes = isBefore(start, thirtyMinutesFromNow);
      const missedTimeThreshold = subHours(currentTimeAtVenue, 72);
      const isOlderThanThreshold = isBefore(start, missedTimeThreshold);
      const isOlderThan30Days = isBefore(start, subDays(currentTimeAtVenue, FEEDBACK_REPORT_AGE_LIMIT_DAYS));
      const lastUpdatedDate = lastUpdated || createdOn;

      return {
        end: parseISO(endDatetime),
        resourceId: teacherId,
        start,
        type: CalendarEventType.RESERVATION,
        data: {
          fullName: lastName ? `${firstName} ${lastName}` : firstName,
          isAttended: startsWithinThirtyMinutes && attendanceStatus === true,
          isCancelled: [
            ReservationCancellationType.USER_CANCELLED,
            ReservationCancellationType.USER_LATE_CANCEL,
            ReservationCancellationType.USER_LATE_CANCEL_WAIVE,
            ReservationCancellationType.CLASS_CANCELLED,
            ReservationCancellationType.CLASSTIVITY_CANCELLED,
          ].includes(cancellationType),
          isFirstTime: visitNumberToVenue === 1,
          isInThePast: isBefore(start, currentTimeAtVenue),
          isLateCancelled: cancellationType === ReservationCancellationType.USER_LATE_CANCEL,
          isLateCancelWaived: cancellationType === ReservationCancellationType.USER_LATE_CANCEL_WAIVE,
          isMissed: startsWithinThirtyMinutes && attendanceStatus === false,
          lastUpdated: lastUpdatedDate,
          startsWithinThirtyMinutes,
          isOlderThanThreshold,
          isOlderThan30Days,
          title: restAppointment.className,
          ...restAppointment,
        },
      };
    }
  );

  return [
    ...calendarSyncEvents,
    ...msiTeacherTimeBlocks,
    ...venueTimeBlockEvents,
    ...venueTimeBlockFillerEvents,
    ...appointments,
  ];
}

export function selectBackgroundEvents(state) {
  const activeCalendarView = selectActiveCalendarView(state);
  if (activeCalendarView === Views.WEEK) {
    return [];
  }

  const venueId = selectActiveVenueId(state);

  const { startDate, endDate } = selectAvailabilityCalendarDates(state);
  const key = calendarAvailabilityEntity.defaultKey(null, venueId, startDate, endDate);

  const { loading, data } = calendarAvailabilityEntity.select(state, key) || {};

  if (!data || loading) {
    return [];
  }

  return data.teacherUnavailableTimeBlocks.map(getRBCEventMapper(CalendarEventType.UNAVAILABLE_TIME_BLOCK));
}

export const selectConversationStatus = createSelector(
  (state, reservationId) => ({ ...(state || {}), reservationId }),
  state => {
    const {
      appointments: { sendingMessage },
      reservationId,
    } = state;
    const key = messageUserEntity.defaultKey(null, { reservationId });
    const { data, loading, loaded } = messageUserEntity.select(state, key);
    const { recipientConversationEligible = false, status = true } = (loaded && data) || {};
    const conversationStarted = loaded ? !!status : true;

    return {
      loading,
      loaded,
      conversationStarted,
      recipientConversationEligible,
      sendingMessage,
    };
  }
);

export const selectConversationLimitStatus = createSelector(
  state => state || {},
  state => {
    const { conversationAllowed = false, conversationCount = 0 } =
      (conversationLimitStatusEntity.loaded && conversationLimitStatusEntity.data(state)) || {};
    return {
      conversationAllowed,
      conversationCount,
    };
  }
);

export const selectShowPhoneEdit = createSelector(
  state => state || {},
  state => state.appointments?.updatingPhoneNumber
);
